// src/services/api.js
// Centralized API client for exacto (mirrors nidoux ApiService pattern)
import { getApiBaseUrl } from '../config';

const API_BASE = getApiBaseUrl();

class ApiService {
  constructor() {
    this._backoffUntil = 0;
  }

  async request(endpoint, options = {}) {
    const now = Date.now();
    if (now < this._backoffUntil) {
      const error = new Error('Rate limited – backing off');
      error.status = 429;
      error.isBackoff = true;
      throw error;
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // Handle 429 Too Many Requests
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const backoffMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000;
        this._backoffUntil = Date.now() + backoffMs;
        const error = new Error('Too many requests, please try again later.');
        error.status = 429;
        throw error;
      }

      if (!response.ok) {
        let error;
        try {
          const errorData = await response.json();
          error = new Error(errorData.message || `Request failed with status ${response.status}`);
          error.status = response.status;
          if (errorData.validationErrors) error.validationErrors = errorData.validationErrors;
          if (errorData.errors) error.validationErrors = errorData.errors;
        } catch (jsonError) {
          if (jsonError instanceof Error && jsonError.status) throw jsonError;
          error = new Error(`Request failed with status ${response.status}`);
          error.status = response.status;
        }
        throw error;
      }

      // Some endpoints return no body (204)
      if (response.status === 204) return null;

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return null;
    } catch (error) {
      if (error.status && error.status >= 500) {
        console.error('API Server Error:', error);
      }
      throw error;
    }
  }

  // Helper for multipart form data (file uploads)
  async uploadFile(endpoint, formData) {
    const now = Date.now();
    if (now < this._backoffUntil) {
      const error = new Error('Rate limited – backing off');
      error.status = 429;
      throw error;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // No Content-Type header — browser sets multipart boundary automatically
    });

    if (!response.ok) {
      const error = new Error(`Upload failed with status ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    return null;
  }

  // Helper for blob downloads (PDFs, exports)
  async downloadBlob(endpoint) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = new Error(`Download failed with status ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return await response.blob();
  }

  // ─── Auth ───────────────────────────────────────────────
  async login(accessCode) {
    return this.request('/login', {
      method: 'POST',
      body: JSON.stringify({ accessCode }),
    });
  }

  async verifySmsCode(userId, code) {
    return this.request('/login/verify-sms-code', {
      method: 'POST',
      body: JSON.stringify({ userId, code }),
    });
  }

  async logout() {
    return this.request('/logout');
  }

  // ─── Offers ─────────────────────────────────────────────
  async getOffers() {
    return this.request('/offers');
  }

  async getOffer(offerId) {
    return this.request(`/offers/${offerId}`);
  }

  // Public (unauthenticated) offer fetch — used by /offer/:id voice AI page
  async getPublicOffer(offerId) {
    return this.request(`/public/offers/${offerId}`);
  }

  async createOffer(data) {
    return this.request('/offers/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOffer(offerId, data) {
    return this.request(`/offers/update/${offerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOffer(offerId) {
    return this.request(`/offers/delete/${offerId}`, {
      method: 'DELETE',
    });
  }

  async getGeneratedOffers(offerId) {
    return this.request(`/offers/generated/${offerId}`);
  }

  async saveOfferPlan(offerId, offerPlanData) {
    return this.request(`/offers/saveOfferPlan/${offerId}`, {
      method: 'POST',
      body: JSON.stringify({ offerPlan: offerPlanData }),
    });
  }

  async generateQRCode(offerId, customText) {
    const body = customText ? { customText } : {};
    return this.request(`/offers/generate-qr/${offerId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ─── Offer Uploads ─────────────────────────────────────
  async uploadOfferPhoto(offerId, file) {
    const formData = new FormData();
    formData.append('image', file);
    return this.uploadFile(`/offers/upload-photo/${offerId}`, formData);
  }

  async uploadOfferLogo(offerId, file) {
    const formData = new FormData();
    formData.append('image', file);
    return this.uploadFile(`/offers/upload-logo/${offerId}`, formData);
  }

  async uploadAssistantLogo(offerId, file) {
    const formData = new FormData();
    formData.append('image', file);
    return this.uploadFile(`/offers/upload-assistantLogo/${offerId}`, formData);
  }

  // ─── Contacts / Ingestions ──────────────────────────────
  async ingestContacts(offerId, data) {
    return this.request(`/offers/${offerId}/contacts/ingest`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getIngestions(offerId) {
    return this.request(`/offers/${offerId}/contacts`);
  }

  async deleteIngestion(offerId, ingestionId) {
    return this.request(`/offers/${offerId}/contacts/${ingestionId}`, {
      method: 'DELETE',
    });
  }

  // ─── Chat / Bot ─────────────────────────────────────────
  async createAssistant(description) {
    return this.request('/chat/create-assistant', {
      method: 'POST',
      body: JSON.stringify({ description }),
    });
  }

  async chatWithBot(userId, userMessage) {
    return this.request('/chat/chat-with-bot', {
      method: 'POST',
      body: JSON.stringify({ userId, userMessage }),
    });
  }

  async chatWithDynamicBot(userId, assistantId, userMessage) {
    return this.request('/chat/chat-with-dynamic-bot', {
      method: 'POST',
      body: JSON.stringify({ userId, assistantId, userMessage }),
    });
  }

  // ─── Games ──────────────────────────────────────────────
  async createGame(payload) {
    return this.request('/public/games/create', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  async saveRunnerData(gameId, runData) {
    return this.request(`/public/games/${gameId}/runner-data`, {
      method: 'POST',
      body: JSON.stringify({ runData }),
    });
  }

  async saveCatcherData(gameId, catchData) {
    return this.request(`/public/games/${gameId}/catcher-data`, {
      method: 'POST',
      body: JSON.stringify({ catchData }),
    });
  }

  async completeGame(gameId) {
    return this.request(`/public/games/${gameId}/complete`, {
      method: 'POST',
    });
  }

  // ─── Scraper / Finder ───────────────────────────────────
  async startFinder(data) {
    return this.request('/scraper/finder/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFinderStatus(jobId) {
    return this.request(`/scraper/finder/${jobId}`);
  }

  async getFinderUrls(jobId, page = 1, limit = 100, search = '') {
    const params = new URLSearchParams({ page, limit, search });
    return this.request(`/scraper/finder/${jobId}/urls?${params}`);
  }

  async cancelFinder(jobId) {
    return this.request(`/scraper/finder/${jobId}/cancel`, {
      method: 'POST',
    });
  }

  async useFinderForBatch(jobId, batchName, maxUrls) {
    return this.request(`/scraper/finder/${jobId}/use-for-batch`, {
      method: 'POST',
      body: JSON.stringify({ batchName, maxUrls }),
    });
  }

  async getAllFinders() {
    return this.request('/scraper/finder');
  }

  // ─── Scraper / Single jobs ──────────────────────────────
  async startScrape(data) {
    return this.request('/scraper/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getScrapeJobStatus(jobId) {
    return this.request(`/scraper/jobs/${jobId}`);
  }

  async getScrapeBusinesses(jobId, page = 1, limit = 50, search = '') {
    const params = new URLSearchParams({ page, limit, search });
    return this.request(`/scraper/jobs/${jobId}/businesses?${params}`);
  }

  async cancelScrapeJob(jobId) {
    return this.request(`/scraper/jobs/${jobId}/cancel`, {
      method: 'POST',
    });
  }

  async getScrapeJobs(page = 1, limit = 20) {
    return this.request(`/scraper/jobs?page=${page}&limit=${limit}`);
  }

  getScrapeExportUrl(jobId, format) {
    return `${API_BASE}/scraper/jobs/${jobId}/export/${format}`;
  }

  async exportScrapeJob(jobId, format) {
    return this.request(`/scraper/jobs/${jobId}/export/${format}`, {
      method: 'POST',
    });
  }

  // ─── Scraper / Batches ──────────────────────────────────
  async startScrapeBatch(urls, name) {
    return this.request('/scraper/batch/start', {
      method: 'POST',
      body: JSON.stringify({ urls, name }),
    });
  }

  async getScrapeBatchStatus(batchId) {
    return this.request(`/scraper/batch/${batchId}`);
  }

  async cancelScrapeBatch(batchId) {
    return this.request(`/scraper/batch/${batchId}/cancel`, {
      method: 'POST',
    });
  }

  async pauseScrapeBatch(batchId) {
    return this.request(`/scraper/batch/${batchId}/pause`, {
      method: 'POST',
    });
  }

  async resumeScrapeBatch(batchId) {
    return this.request(`/scraper/batch/${batchId}/resume`, {
      method: 'POST',
    });
  }

  async getScrapeBatches(page = 1, limit = 20) {
    return this.request(`/scraper/batch?page=${page}&limit=${limit}`);
  }

  getScrapeBatchExportUrl(batchId, format) {
    return `${API_BASE}/scraper/batch/${batchId}/export/${format}`;
  }

  // ─── Real Estate / Rentability ──────────────────────────
  async startRealEstate(data) {
    return this.request('/realestate/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRealEstateJob(jobId) {
    return this.request(`/realestate/jobs/${jobId}`);
  }

  async getRealEstateJobs(page = 1, limit = 20) {
    return this.request(`/realestate/jobs?page=${page}&limit=${limit}`);
  }

  async getRealEstateListings(jobId, page = 1, limit = 50, listingType = '', search = '') {
    const params = new URLSearchParams({ page, limit, listingType, search });
    return this.request(`/realestate/jobs/${jobId}/listings?${params}`);
  }

  async getRealEstateRentability(jobId) {
    return this.request(`/realestate/jobs/${jobId}/rentability`);
  }

  // On-demand AI analysis + per-area comments (cached server-side per selection).
  //   opts.keys  — optional subset of row keys (one = single verdict, a few = comparison)
  //   opts.force — bypass the per-selection cache
  async aiRankRealEstate(jobId, { force = false, keys = null } = {}) {
    return this.request(`/realestate/jobs/${jobId}/ai-rank`, {
      method: 'POST',
      body: JSON.stringify({ force, keys }),
    });
  }

  async suggestRealEstateLocations(query, limit = 8) {
    const params = new URLSearchParams({ q: query, limit });
    return this.request(`/realestate/locations/suggest?${params}`);
  }

  async cancelRealEstateJob(jobId) {
    return this.request(`/realestate/jobs/${jobId}/cancel`, {
      method: 'POST',
    });
  }

  async deleteRealEstateJob(jobId) {
    return this.request(`/realestate/jobs/${jobId}`, {
      method: 'DELETE',
    });
  }

  getRealEstateExportUrl(jobId, format) {
    return `${API_BASE}/realestate/jobs/${jobId}/export/${format}`;
  }

  // ─── Real Estate / Rentability — Phase 2 (paid product) ─────
  // All endpoints below are OPTIONAL: the UI renders-if-present and degrades
  // gracefully on 404 / network failure (the backend is wired separately).

  // Per-deal financial model: monthly cash-flow, cash-on-cash, tax-regime table.
  //   payload — { jobId?, listingId?, price, monthlyRent, downPayment?, ltv?,
  //               ratePct?, termYears?, regime?, ... } (shape tolerant)
  async dealCalc(payload) {
    return this.request('/realestate/deal-calc', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  // Subscription entitlements (plan + per-feature limits). Callers should default
  // to a 'free' shape if this throws/404s.
  async getEntitlements() {
    return this.request('/realestate/entitlements');
  }

  // Saved searches / instant alerts.
  async listAlerts() {
    return this.request('/realestate/saved-searches');
  }

  async createAlert(body) {
    return this.request('/realestate/saved-searches', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  async updateAlert(id, body) {
    return this.request(`/realestate/saved-searches/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  async deleteAlert(id) {
    return this.request(`/realestate/saved-searches/${id}`, {
      method: 'DELETE',
    });
  }

  // Run a saved search now (returns fresh matches / triggers a digest server-side).
  async runAlert(id) {
    return this.request(`/realestate/saved-searches/${id}/run-now`, {
      method: 'POST',
    });
  }

  // ─── Property Dossier (moteurimmo-style) ────────────────
  async startDossier(data) {
    return this.request('/dossier/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDossierJob(jobId) {
    return this.request(`/dossier/jobs/${jobId}`);
  }

  async getDossierJobs(page = 1, limit = 20) {
    return this.request(`/dossier/jobs?page=${page}&limit=${limit}`);
  }

  async getDossierListings(jobId, page = 1, limit = 30, search = '', sort = '') {
    const params = new URLSearchParams({ page, limit, search, sort });
    return this.request(`/dossier/jobs/${jobId}/listings?${params}`);
  }

  async getListingDossier(listingId) {
    return this.request(`/dossier/listings/${listingId}/dossier`);
  }

  async cancelDossierJob(jobId) {
    return this.request(`/dossier/jobs/${jobId}/cancel`, {
      method: 'POST',
    });
  }

  getDossierExportUrl(jobId, format) {
    return `${API_BASE}/dossier/jobs/${jobId}/export/${format}`;
  }

  // ─── Letters / Batch PDF ────────────────────────────────
  async createLetterJob(offerId, options = {}) {
    return this.request('/letters/jobs', {
      method: 'POST',
      body: JSON.stringify({ offerId, ...options }),
    });
  }

  async getLetterJob(jobId) {
    return this.request(`/letters/jobs/${jobId}`);
  }

  getLetterJobDownloadUrl(jobId) {
    return `${API_BASE}/letters/jobs/${jobId}/download`;
  }

  async downloadLetterJob(jobId) {
    return this.downloadBlob(`/letters/jobs/${jobId}/download`);
  }

  // ─── Realtime / Save Generated ──────────────────────────
  // Mint an ephemeral API key for the OpenAI Realtime voice session.
  // Returns `{ value, expires_at, session }` from /v1/realtime/client_secrets.
  // `isPublic` kept for call-site compatibility — both admin and public-share
  // flows currently use the public mint endpoint.
  // eslint-disable-next-line no-unused-vars
  async createRealtimeSession(payload = {}, isPublic = false) {
    return this.request('/public/chat/create-realtime-session', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async saveGeneratedOffer(originalOfferId, collectedData) {
    return this.request('/public/save-generated', {
      method: 'POST',
      body: JSON.stringify({ originalOfferId, collectedData }),
    });
  }

  async saveVoiceSession(payload) {
    return this.request('/public/voice-sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // GDPR right-to-be-forgotten. Id is the _id returned by saveVoiceSession.
  async deleteVoiceSession(id) {
    return this.request(`/public/voice-sessions/${id}`, { method: 'DELETE' });
  }

  async getVoiceSessions(offerId, limit = 25) {
    return this.request(`/offers/${offerId}/voice-sessions?limit=${limit}`);
  }

  async getActiveMaintenance() {
    return this.request('/maintenance/active');
  }
}

const api = new ApiService();
export default api;
